import { ReactNode, Component } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { isMobile, isTablet } from 'react-device-detect';
import ApplicationConfig from 'providers/useConfig/ApplicationConfig';
import { PlayOption, PlayOptionsData } from 'types/CommonTypes';
import { RecordingType } from 'types/RecordingTypes';
import { ModuleConfigs } from 'types/Config';
import {
    Channel,
    DetailEpisode,
    Listing,
    MovieDetails,
    ProgramDetails,
    RecordingDetails,
    ResolutionType,
    TVSeriesDetailEpisode,
    Episode,
} from 'types/Asset';
import { BitrateTrack, CatchupPlayerAssetInfo, RecordingPlayerAssetInfo, PlayingAssetType, Track, VodPlayerAssetInfo } from 'types/Player';
import { StreamUseCase } from 'types/ApiTypes';
import { isFuture, isPast } from './fnDate';
import { DAYS, SECONDS } from './TimeUnit';
import translate from './fnTranslate';
import { encodeId } from './fnUrl';
import { isCatchupPlayerAsset, isRecordingPlayerAsset } from './fnTypeGuards';

const notIgnoredVariables = ['collectionid', 'collectiontype'];

const mobileWidth = 240;
const mobileHeight = 135;

const tabletWidth = 288;
const tabletHeight = 162;

const desktopWidth = 400;
const desktopHeight = 225;

type RouteMatch = { path: string; params: Record<string, string> };

export const getParentRoute = (route: RouteMatch, params, setBackParamValues?) => {
    if (route) {
        let path = `${route.path}`;

        // is not collection page parent
        if (!path.startsWith('collection') && !path.startsWith('/collection')) {
            return path;
        }
        if (params && setBackParamValues) {
            // replace path variables with values
            // eslint-disable-next-line no-restricted-syntax
            for (const key in params) {
                if (key in params) {
                    if (key != null && notIgnoredVariables.indexOf(key) !== -1) {
                        path = path.replace(`:${key}`, params[key]);
                    }
                }
            }
        }
        return encodeURI(path);
    }

    return '/';
};

/**
 * Use this function for replay capability check
 *
 * @param start - start time of the broadcast in milliseconds
 * @param isChannelReplayCapable - boolean is replay capable (has HAS_REPLAY alias with TRUE value)
 * @param replayDays - number of replay days in the past (TimeMachine / TimeMachinePlusBased), or -1
 * if the user has no subscription tto that product
 */
export const isCatchUpCapable = (start: number, isChannelReplayCapable: boolean, replayDays: number) => {
    if (!isChannelReplayCapable || replayDays === -1) return false;

    return start >= Date.now() - DAYS.toMillis(replayDays);
};

export type ListingCapability = {
    replayUntil: number | null;
    recordingUntil: number | null;
    restartUntil: number | null;
    timeshift: boolean;
};

export const getListingCapability = (listing: { start?: number; duration?: number }, channel: Channel): ListingCapability => {
    const start = listing?.start;
    const duration = listing?.duration;

    if (!channel || start == null || duration == null) {
        return { replayUntil: null, recordingUntil: null, restartUntil: null, timeshift: false };
    }

    return {
        replayUntil: channel.replay ? start + (channel.replay_depth ?? 0) : null,
        recordingUntil: channel.recording ? start + duration + (channel.recording_depth ?? 0) : null,
        restartUntil: channel.instant_restart ? start + duration : null,
        timeshift: !!channel.timeshift,
    };
};

export const getBroadcastStreamUseCase = (endTime: Date | number): StreamUseCase =>
    isPast(endTime) ? StreamUseCase.REPLAY : StreamUseCase.RESTART;

/**
 * Use this function to transform camel case string to snake case
 *
 * @param str - input
 * @return snake cased string
 */
export const camelToSnakeCase = (str: string) => {
    let result = str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    if (result.indexOf('_') === 0) {
        result = result.slice(1, result.length);
    }
    return result;
};

/**
 * Use to build episode title included the season / episode number with translation
 *
 * @param title
 * @param seasonNumber
 * @param episodeNumber
 */
export const buildEpisodeTitle = (title?: string, seasonNumber?: number, episodeNumber?: number) => {
    const partSeason = seasonNumber != null && seasonNumber > 0 ? `${translate('HOVER_SEASON')}${seasonNumber} ` : '';
    const partEpisode = episodeNumber != null ? `${translate('HOVER_EPISODE')}${episodeNumber} ` : '';
    const partTitle = title || '';

    return `${partSeason}${partEpisode}${partTitle}`.trim();
};

/**
 * Use this function to return the image remote path or the local image
 * according to de passed data
 *
 */

export const getLocalOrRemoteImagePath = (imagePath: string, images: {}) => {
    if (imagePath) {
        if (imagePath.startsWith('http')) {
            // is link
            return imagePath;
        }

        // is local image
        return images[imagePath];
    }

    return null;
};

/**
 * Find the replay window length by product
 *
 */
export const getReplayWindowLengthByProduct = (replayProduct: string): number => {
    if (!replayProduct) return -1;
    const type = `replay_window_${camelToSnakeCase(replayProduct)}`;

    if (!ApplicationConfig.app_config.product_settings[type]) return -1;

    return ApplicationConfig.app_config.product_settings[type];
};

export const getPlayOptionByEventId = (option: PlayOptionsData, eventId: string): PlayOption =>
    option?.options?.find((d) => d.id === eventId);
const getPastOrRunningOptionsSorted = (option: PlayOptionsData): PlayOption[] => {
    if (!option || !option.options.length) {
        return [];
    }

    return (
        option.options
            // filter out future events
            ?.filter((opt) => !isFuture(opt.startTime))
            // sort by time
            ?.sort((a, b) => b.startTime - a.startTime)
    );
};
const getPlayOptionFromClosestToNow = (option: PlayOptionsData): PlayOption => getPastOrRunningOptionsSorted(option)?.[0];
export const getPlayOptionFromClosestToNowFromEntitledChannel = (option: PlayOptionsData): PlayOption =>
    getPastOrRunningOptionsSorted(option)?.filter((opt) => opt.channel.subscription)?.[0] ?? getPlayOptionFromClosestToNow(option);

export const getUniquePlayOptions = (input: Listing[], replayWindowLength, referenceEvent: string, sort = false): Listing[] => {
    let filteredPlayOptions: Listing[] = [];

    if (input) {
        input
            // filter out future options
            .filter((option) => !isFuture(new Date(option.start)))

            // filter out options out of replay window
            // TODO: this needs to be addressed once entitlements are done(?). Uses getReplayWindowTimeFrame from authContext
            // .filter(option => MILLISECONDS.toDays(Date.now() - option.start) <= replayWindowLength)

            .sort((a, b) => b.start - a.start)

            // filter out reruns
            .forEach((option) => {
                const match = filteredPlayOptions.find((item) => item.channel_id === option.channel_id);

                if (!match) {
                    filteredPlayOptions.push(option);
                } else if (referenceEvent && option.id === referenceEvent) {
                    const index = filteredPlayOptions.indexOf(match);
                    filteredPlayOptions[index] = option;
                }
            });

        filteredPlayOptions = filteredPlayOptions?.sort((a, b) => (sort ? b.start - a.start : 0));

        // sort not subscribed channels to the end
        filteredPlayOptions = filteredPlayOptions?.sort((a, b) => {
            if (a.channel?.subscription && !b.channel?.subscription) return -1;
            if (!a.channel?.subscription && b.channel?.subscription) return 1;
            return 0;
        });

        if (referenceEvent) {
            // move ref event to the first place
            const index = filteredPlayOptions.findIndex((option) => option.id === referenceEvent);
            if (index > 0) {
                const match = filteredPlayOptions.splice(index, 1)?.[0];
                if (match) {
                    filteredPlayOptions.unshift(match);
                }
            }
        }
    }

    return filteredPlayOptions;
};

export const generateUUID = () => {
    const lut = [];
    for (let i = 0; i < 256; i += 1) {
        lut[i] = (i < 16 ? '0' : '') + i.toString(16);
    }
    const d0 = (Math.random() * 0xffffffff) | 0;
    const d1 = (Math.random() * 0xffffffff) | 0;
    const d2 = (Math.random() * 0xffffffff) | 0;
    const d3 = (Math.random() * 0xffffffff) | 0;
    return `${lut[d0 & 0xff] + lut[(d0 >> 8) & 0xff] + lut[(d0 >> 16) & 0xff] + lut[(d0 >> 24) & 0xff]}-${lut[d1 & 0xff]}${
        lut[(d1 >> 8) & 0xff]
    }-${lut[((d1 >> 16) & 0x0f) | 0x40]}${lut[(d1 >> 24) & 0xff]}-${lut[(d2 & 0x3f) | 0x80]}${lut[(d2 >> 8) & 0xff]}-${
        lut[(d2 >> 16) & 0xff]
    }${lut[(d2 >> 24) & 0xff]}${lut[d3 & 0xff]}${lut[(d3 >> 8) & 0xff]}${lut[(d3 >> 16) & 0xff]}${lut[(d3 >> 24) & 0xff]}`;
};

export const randomIntBetween = (min: number, max: number): number => Math.floor(Math.random() * (max - min + 1)) + min;

export const dynamicCollectionIdToTitle = (id: string): string => {
    if (!id) return null;

    const split = id.split('-');
    if (!split.length) return null;

    return split[split.length - 1].replace(/\+/g, ' ');
};

export const getRecordingIconName = (type: RecordingType) => {
    switch (type) {
        case 'RECORDED':
            return 'icoRecordedRecording';
        case 'ONGOING':
            return 'icoOngoingRecording';
        case 'SCHEDULED':
            return 'icoScheduledRecording';
        default:
            return null;
    }
};

/**
 * Clones the module configurations and injects the details and the imdb rating receiver
 *
 * @param details
 * @param pageModules
 * @param ratingReceiver
 * @param itemsReceiver
 */
export const detailPageModuleResolver = (
    details: RecordingDetails | ProgramDetails | MovieDetails | DetailEpisode | TVSeriesDetailEpisode,
    pageModules,
    ratingReceiver,
    itemsReceiver?
): ModuleConfigs[] => {
    let modules: ModuleConfigs[] = null;

    if (details) {
        modules = cloneDeep(pageModules);
        modules.forEach((module) => {
            if (module.datasource.request) {
                if (!module.datasource.request.variables) {
                    module.datasource.request.variables = {};
                }

                // eslint-disable-next-line dot-notation
                module.datasource.request.variables['details'] = details;

                // eslint-disable-next-line dot-notation
                module.datasource.request.variables['ratingReceiver'] = ratingReceiver;

                if (itemsReceiver) {
                    // eslint-disable-next-line dot-notation
                    module.datasource.request.variables['itemsReceiver'] = itemsReceiver;
                }
            }
        });
    }

    return modules;
};

export const getResolution = (resolution: any, isHD: boolean): ResolutionType => {
    if (ResolutionType[resolution]) {
        return resolution;
    }

    return isHD ? ResolutionType.HD : ResolutionType.SD;
};

export const getCurrencySymbol = (currencyName: string): string => {
    const nameSymbolMap = {
        USD: '$', // US Dollar
        EUR: '€', // Euro
        CRC: '₡', // Costa Rican Colón
        GBP: '£', // British Pound Sterling
        ILS: '₪', // Israeli New Sheqel
        INR: '₹', // Indian Rupee
        JPY: '¥', // Japanese Yen
        KRW: '₩', // South Korean Won
        NGN: '₦', // Nigerian Naira
        PHP: '₱', // Philippine Peso
        PLN: 'zł', // Polish Zloty
        PYG: '₲', // Paraguayan Guarani
        THB: '฿', // Thai Baht
        UAH: '₴', // Ukrainian Hryvnia
        VND: '₫', // Vietnamese Dong
    };

    if (!currencyName) return ApplicationConfig.app_config.locale_settings.currency_sign;
    if (!(currencyName in nameSymbolMap)) return ApplicationConfig.app_config.locale_settings.currency_sign;
    return nameSymbolMap[currencyName];
};

export const getDefaultTrackStyling = () => {
    const style = new window.chrome.cast.media.TextTrackStyle();
    style.backgroundColor = '#00000000';
    style.edgeType = window.chrome.cast.media.TextTrackEdgeType.DROP_SHADOW;
    style.fontGenericFamily = window.chrome.cast.media.TextTrackFontGenericFamily.SANS_SERIF;
    style.edgeColor = '#000000';
    style.fontStyle = window.chrome.cast.media.TextTrackFontStyle.BOLD;
    return style;
};

export const compareTracks = (a: Track, b: Track) => {
    if ((a == null && b != null) || (b == null && a != null)) return false;
    return a.language === b.language && a.role === b.role && a.channelsCount === b.channelsCount;
};

export const compareBitrateTracks = (a: BitrateTrack, b: BitrateTrack) => {
    if ((a == null && b != null) || (b == null && a != null)) return false;
    return a.height === b.height;
};

export const getInitials = (fullName) => {
    const names = fullName.split(' ');
    const initials = names.map((initial) => initial.charAt(0));
    return initials.join('');
};

export const getLanguage = (code: string, translateKey: string) => {
    if (translateKey.startsWith('LANGUAGE')) {
        return code;
    }
    return translateKey;
};

export const getThumbnailSize = (thumb: any) => {
    const width = isMobile ? mobileWidth : isTablet ? tabletWidth : desktopWidth;
    const height = isMobile ? mobileHeight : isTablet ? tabletHeight : desktopHeight;

    const { uris, positionX, positionY } = thumb;

    const widthPositionX = isMobile ? positionX * 1.5 : isTablet ? positionX * 1.8 : positionX * 2.5;
    const heightPositionY = isMobile ? positionY * 1.5 : isTablet ? positionY * 1.8 : positionY * 2.5;

    // eslint-disable-next-line prefer-const
    let [path, query] = uris[0]?.split('?') ?? [];

    if (query) {
        query = `sx=${width}&sy=${height}&scale=${width}x${height}`;
    } else {
        query = `sx=${width}&sy=${height}`;
    }

    const url = [path, query].join('?');

    return {
        ...thumb,
        width,
        height,
        uris: [url],
        positionX: widthPositionX,
        positionY: heightPositionY,
    };
};

export const getNextEpisode = (seasons: any, currentSeason: number, currentEpisode: number) => {
    const season = seasons?.find((item) => item.number === currentSeason);
    let nextEpisode = season?.episodes.find((item) => item.episodeNumber === currentEpisode + 1);

    if (!nextEpisode) {
        const nextSeason = seasons?.find((item) => item.number === currentSeason + 1);

        if (nextSeason) {
            nextEpisode = nextSeason?.episodes[0];
            return nextEpisode;
        }
        return null;
    }

    return nextEpisode;
};

export const getMiniPlayerProps = (asset: any, type?: PlayingAssetType) => {
    let inferredType = type;

    if (!inferredType) {
        if (asset.channelId) {
            inferredType = PlayingAssetType.LIVE;
        } else if (asset.eventId) {
            inferredType = PlayingAssetType.CATCHUP;
        } else if (asset.episodeId) {
            inferredType = PlayingAssetType.VOD;
        } else if (asset.recordingId) {
            inferredType = PlayingAssetType.RECORDING;
        }
    }

    if (!inferredType) {
        return null;
    }

    switch (inferredType) {
        case PlayingAssetType.VOD:
        case PlayingAssetType.TRAILER:
            return { type: inferredType, params: { assetId: encodeId(asset.episodeId ?? asset.id) } };
        case PlayingAssetType.CATCHUP:
            return { type: inferredType, params: { broadcastId: encodeId(asset.eventId ?? asset.id) } };
        case PlayingAssetType.LIVE:
            return { type: inferredType, params: { channelId: encodeId(asset.channelId) } };
        case PlayingAssetType.RECORDING:
            return { type: inferredType, params: { recordingId: encodeId(asset.recordingId) } };
        default:
            return null;
    }
};

export const selectDefaultSubtitles = (
    prefferedSubtitles: { primarySubtitlesLanguage: string },
    subtitleTracks: Track[],
    accessibility: boolean,
    hohRoles: string[]
) => {
    const primaryLanguage = subtitleTracks.filter((track) => track.language === prefferedSubtitles.primarySubtitlesLanguage);

    let accessibilityTrack: Track;

    const isHearingImpaired = (track: Track) =>
        hohRoles.includes(track.role) || track.raw?.roles?.some((role: string) => hohRoles.includes(role));

    if (accessibility) {
        accessibilityTrack = primaryLanguage.find(isHearingImpaired) ?? subtitleTracks.find(isHearingImpaired);
    }

    return accessibilityTrack ?? primaryLanguage[0] ?? subtitleTracks[0];
};

export const selectDefaultAudio = (
    prefferedAudio: { primaryAudioLanguage: string },
    audioTracks: Track[],
    accessibility: boolean,
    visuallyImpairedRoles: string[]
) => {
    const primaryLanguage = audioTracks.filter((track) => track.language === prefferedAudio.primaryAudioLanguage);
    let accessibilityTrack: Track;

    const isVisuallyImpaired = (track: Track) =>
        visuallyImpairedRoles.includes(track.role) || track.raw?.roles?.some((role: string) => visuallyImpairedRoles.includes(role));

    if (accessibility) {
        accessibilityTrack = primaryLanguage.find(isVisuallyImpaired) ?? audioTracks.find(isVisuallyImpaired);
    }

    return accessibilityTrack ?? primaryLanguage[0] ?? audioTracks[0];
};

export const isAssetWithinPaddingWindow = (playingAsset: CatchupPlayerAssetInfo | RecordingPlayerAssetInfo) => {
    if (isCatchupPlayerAsset(playingAsset) || isRecordingPlayerAsset(playingAsset)) {
        const { endTime, pre_padding, post_padding } = playingAsset;
        const paddingTime = SECONDS.toMillis(post_padding ?? 0) + SECONDS.toMillis(pre_padding ?? 0);
        const now = Date.now();
        const endTimeInMilliseconds = SECONDS.toMillis(endTime);
        if (endTimeInMilliseconds + paddingTime > now) {
            return true;
        }
    }
    return false;
};

// Wish there was better way to distinguish trailers/extras etc from episodes API :))
const TRAILER_KEYWORDS = ['trailer', 'treiler', 'extra', 'bonus', 'preview'];

export const isEpisodeTrailer = (episode: Episode | DetailEpisode | TVSeriesDetailEpisode | VodPlayerAssetInfo) => {
    const title = ((episode as DetailEpisode)?.episode_title || episode?.title || '').toLowerCase();
    return TRAILER_KEYWORDS.some((keyword) => title.includes(keyword));
};

export const hasProgressBar = (progressBar: ReactNode) => {
    const progressBarComp = progressBar as unknown as Component<{
        percentage?: number;
        startTime?: number;
        displayWithNullProgress?: boolean;
    }>;
    const progressBarProps = progressBarComp?.props;

    if (!progressBarProps) {
        return false;
    }

    if (progressBarProps.startTime) {
        return true;
    }

    if (progressBarProps.displayWithNullProgress) {
        return !Number.isNaN(progressBarProps.percentage);
    }

    return (progressBarProps.percentage ?? 0) >= 1;
};

export const secondsToTimeString = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    let timeString = '';

    if (hours > 0) {
        timeString += `${hours} ${hours === 1 ? 'hour' : 'hours'} `;
    }
    if (minutes > 0) {
        timeString += `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
    }

    return timeString || '0 minutes';
};
